import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useVendorAccess } from '@/hooks/useVendorAccess';
import { formatPrice, formatPriceWithCurrency, getCurrencySymbol } from '@/utils/currency';
import { useCurrency, type Currency } from '@/hooks/useCurrency';
import { 
  DollarSign, Wallet, AlertCircle, CheckCircle, XCircle, 
  Clock, RefreshCw, Plus, CreditCard, Smartphone, Info
} from 'lucide-react';
import { toast } from 'sonner';

interface VendorBalance {
  available_amount_cents: number;
  pending_amount_cents: number;
  currency: string;
}

interface WithdrawalRequest {
  id: string;
  amount_cents: number;
  currency: string;
  payment_method: 'bank_transfer' | 'mobile_money' | 'other';
  bank_name: string | null;
  account_number: string | null;
  account_name: string | null;
  mobile_money_provider: string | null;
  mobile_money_number: string | null;
  payment_notes: string | null;
  status: 'pending' | 'approved' | 'rejected' | 'processing' | 'completed' | 'cancelled';
  requested_at: string;
  reviewed_at: string | null;
  completed_at: string | null;
  admin_notes: string | null;
  rejection_reason: string | null;
  transaction_reference: string | null;
}

interface VendorTransactionBalance {
  currency: string;
  type: 'credit' | 'debit' | 'withdrawal' | 'refund' | 'adjustment';
  net_amount_cents: number;
}

export const VendorWithdrawalRequest = () => {
  const { user } = useAuth();
  const { vendorIds, loading: accessLoading } = useVendorAccess();
  const { displayCurrency, convertPrice } = useCurrency();
  const [balance, setBalance] = useState<VendorBalance | null>(null);
  const [requests, setRequests] = useState<WithdrawalRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [showRequestDialog, setShowRequestDialog] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [feeRates, setFeeRates] = useState({ withdrawal: 0.025, maishapayB2c: 0.035 });
  const [vendorDefaults, setVendorDefaults] = useState({
    business_name: '',
    business_phone: '',
    default_mobile_provider: 'Vodacom',
    default_mobile_money_number: '',
    default_bank_account: '',
    default_bank_name: '',
    default_bank_first_name: '',
    default_bank_last_name: ''
  });

  const [form, setForm] = useState({
    amount: '',
    payment_method: 'mobile_money' as const,
    bank_name: vendorDefaults.default_bank_name,
    account_number: vendorDefaults.default_bank_account,
    account_name: '',
    mobile_money_provider: vendorDefaults.default_mobile_provider,
    mobile_money_number: vendorDefaults.business_phone,
    payment_notes: ''
  });

  const targetVendorId = vendorIds.length > 0 ? vendorIds[0] : user?.id ?? null;

  const buildBankHolderName = (defaults: {
    business_name: string;
    default_bank_first_name: string;
    default_bank_last_name: string;
  }) => {
    const representativeName = `${defaults.default_bank_first_name} ${defaults.default_bank_last_name}`.trim();
    return representativeName || defaults.business_name || '';
  };

  useEffect(() => {
    if (user?.id && !accessLoading && targetVendorId) {
      fetchData();
    }
  }, [user?.id, accessLoading, targetVendorId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      await Promise.all([fetchBalance(), fetchRequests(), fetchVendorDefaults(), fetchFeeRates()]);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const fetchFeeRates = async () => {
    const readNum = (raw: unknown, fallback: number): number => {
      if (typeof raw === 'number' && Number.isFinite(raw)) return raw;
      if (typeof raw === 'string') { const n = Number(raw); return Number.isFinite(n) ? n : fallback; }
      if (raw && typeof raw === 'object' && 'value' in (raw as any)) return readNum((raw as any).value, fallback);
      return fallback;
    };
    const { data } = await supabase
      .from('site_settings')
      .select('key, value')
      .in('key', ['withdrawal_fee_percentage', 'maishapay_b2c_fee_percentage']);
    const map: Record<string, number> = {};
    (data || []).forEach((row: any) => { map[row.key] = readNum(row.value, 0); });
    setFeeRates({
      withdrawal: (map['withdrawal_fee_percentage'] ?? 2.5) / 100,
      maishapayB2c: (map['maishapay_b2c_fee_percentage'] ?? 3.5) / 100,
    });
  };

  const fetchVendorDefaults = async () => {
    if (!targetVendorId) {
      return;
    }

    const { data, error } = await supabase
      .from('profiles')
      .select('business_name, business_phone, default_mobile_provider, default_mobile_money_number, default_bank_account, default_bank_name, default_bank_first_name, default_bank_last_name')
      .eq('id', targetVendorId)
      .maybeSingle();

    if (error) {
      console.error('Error fetching vendor defaults:', error);
      return;
    }

    const defaults = {
      business_name: data?.business_name || '',
      business_phone: data?.business_phone || '',
      default_mobile_provider: data?.default_mobile_provider || 'Vodacom',
      default_mobile_money_number: (data as any)?.default_mobile_money_number || data?.business_phone || '',
      default_bank_account: data?.default_bank_account || '',
      default_bank_name: data?.default_bank_name || '',
      default_bank_first_name: data?.default_bank_first_name || '',
      default_bank_last_name: data?.default_bank_last_name || ''
    };

    const holderName = buildBankHolderName(defaults);

    setVendorDefaults(defaults);
    setForm((currentForm) => ({
      ...currentForm,
      bank_name: defaults.default_bank_name || currentForm.bank_name,
      account_number: defaults.default_bank_account || currentForm.account_number,
      account_name: holderName || currentForm.account_name,
      mobile_money_provider: defaults.default_mobile_provider || currentForm.mobile_money_provider,
      mobile_money_number: defaults.default_mobile_money_number || defaults.business_phone || currentForm.mobile_money_number
    }));
  };

  const fetchBalance = async () => {
    if (!targetVendorId) return;

    const { data, error } = await supabase
      .from('vendor_balances')
      .select('available_amount_cents, pending_amount_cents, currency')
      .eq('vendor_id', targetVendorId)
      .single();

    if (error && error.code !== 'PGRST116') {
      console.error('Error fetching balance:', error);
      return;
    }

    if (data) {
      setBalance(data);
      return;
    }

    const { data: transactionData, error: transactionError } = await supabase
      .from('vendor_transactions')
      .select('currency, type, net_amount_cents')
      .eq('vendor_id', targetVendorId);

    if (transactionError) {
      console.error('Error fetching transaction balance fallback:', transactionError);
      setBalance(null);
      return;
    }

    const transactions = (transactionData || []) as VendorTransactionBalance[];
    const fallbackAvailableAmount = transactions.reduce((total, transaction) => {
      if (transaction.type === 'credit') {
        return total + convertPrice(transaction.net_amount_cents, transaction.currency as any, displayCurrency);
      }

      if (transaction.type === 'withdrawal') {
        return total - convertPrice(Math.abs(transaction.net_amount_cents), transaction.currency as any, displayCurrency);
      }

      return total;
    }, 0);

    setBalance({
      available_amount_cents: Math.max(0, Math.round(fallbackAvailableAmount)),
      pending_amount_cents: 0,
      currency: displayCurrency
    });
  };

  const fetchRequests = async () => {
    if (!targetVendorId) return;

    const { data, error } = await supabase
      .from('vendor_withdrawal_requests')
      .select('*')
      .eq('vendor_id', targetVendorId)
      .order('requested_at', { ascending: false });

    if (error) {
      console.error('Error fetching requests:', error);
      return;
    }

    setRequests((data || []) as WithdrawalRequest[]);
  };

  const handleSubmit = async () => {
    try {
      if (!user?.id || !targetVendorId) {
        toast.error('Utilisateur non authentifie');
        return;
      }

      const balanceCurrency = (balance?.currency || displayCurrency) as Currency;
      const availableAmountDisplayCents = convertPrice(
        balance?.available_amount_cents || 0,
        balanceCurrency,
        displayCurrency
      );
      const minimumWithdrawalDisplayCents = convertPrice(1000, 'CDF', displayCurrency);

      // Validation
      const enteredAmountDisplayCents = Math.round(parseFloat(form.amount) * 100);
      
      if (isNaN(enteredAmountDisplayCents) || enteredAmountDisplayCents <= 0) {
        toast.error('Veuillez entrer un montant valide');
        return;
      }

      if (enteredAmountDisplayCents > availableAmountDisplayCents) {
        toast.error('Montant supérieur au solde disponible');
        return;
      }

      // Minimum withdrawal validation
      if (enteredAmountDisplayCents < minimumWithdrawalDisplayCents) {
        toast.error(`Le montant minimum de retrait est de ${formatPrice(minimumWithdrawalDisplayCents, displayCurrency)}`);
        return;
      }

      const amountCents = convertPrice(enteredAmountDisplayCents, displayCurrency, balanceCurrency);

      if (form.payment_method === 'bank_transfer') {
        if (!form.bank_name || !form.account_number || !form.account_name) {
          toast.error('Veuillez renseigner compte et titulaire dans les parametres du magasin');
          return;
        }
      } else if (form.payment_method === 'mobile_money') {
        if (!form.mobile_money_number) {
          toast.error('Veuillez renseigner le numero mobile');
          return;
        }
      }

      setSubmitting(true);

      const { data: validationData, error: validationError } = await supabase.rpc('validate_withdrawal_amount', {
        p_vendor_id: targetVendorId,
        p_amount_cents: amountCents
      });

      if (validationError) throw validationError;

      const validationResult = validationData?.[0];

      if (!validationResult?.valid) {
        toast.error(validationResult?.error_message || 'Le retrait ne peut pas etre soumis');
        return;
      }

      const { error } = await supabase
        .from('vendor_withdrawal_requests')
        .insert({
          vendor_id: targetVendorId,
          amount_cents: amountCents,
          currency: balanceCurrency,
          payment_method: form.payment_method,
          bank_name: form.payment_method === 'bank_transfer' ? form.bank_name : null,
          account_number: form.payment_method === 'bank_transfer' ? form.account_number : null,
          account_name: form.payment_method === 'bank_transfer' ? form.account_name : null,
          mobile_money_provider: form.payment_method === 'mobile_money' ? (vendorDefaults.default_mobile_provider || form.mobile_money_provider || null) : null,
          mobile_money_number: form.payment_method === 'mobile_money' ? form.mobile_money_number : null,
          payment_notes: form.payment_notes || null
        });

      if (error) throw error;

      toast.success('Demande de retrait soumise avec succès');
      setShowRequestDialog(false);
      resetForm();
      fetchData();
    } catch (error: any) {
      console.error('Error submitting withdrawal:', error);
      toast.error(error.message || 'Erreur lors de la soumission');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancelRequest = async (requestId: string) => {
    try {
      const { error } = await supabase
        .from('vendor_withdrawal_requests')
        .update({ status: 'cancelled', updated_at: new Date().toISOString() })
        .eq('id', requestId)
        .eq('vendor_id', targetVendorId)
        .eq('status', 'pending');

      if (error) throw error;

      toast.success('Demande annulée');
      fetchRequests();
    } catch (error: any) {
      console.error('Error cancelling request:', error);
      toast.error('Erreur lors de l\'annulation');
    }
  };

  const resetForm = () => {
    setForm({
      amount: '',
      payment_method: 'mobile_money',
      bank_name: vendorDefaults.default_bank_name,
      account_number: vendorDefaults.default_bank_account,
      account_name: buildBankHolderName(vendorDefaults),
      mobile_money_provider: vendorDefaults.default_mobile_provider,
      mobile_money_number: vendorDefaults.default_mobile_money_number || vendorDefaults.business_phone,
      payment_notes: ''
    });
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<string, { variant: any; label: string; icon: any }> = {
      pending: { variant: 'secondary', label: 'En attente', icon: Clock },
      processing: { variant: 'default', label: 'En traitement', icon: RefreshCw },
      approved: { variant: 'default', label: 'Approuvé', icon: CheckCircle },
      completed: { variant: 'default', label: 'Complété', icon: CheckCircle },
      rejected: { variant: 'destructive', label: 'Refusé', icon: XCircle },
      cancelled: { variant: 'outline', label: 'Annulé', icon: XCircle }
    };

    const config = variants[status] || variants.pending;
    const Icon = config.icon;

    return (
      <Badge variant={config.variant} className="flex items-center gap-1 w-fit">
        <Icon className="h-3 w-3" />
        {config.label}
      </Badge>
    );
  };

  const availableAmount = balance?.available_amount_cents || 0;
  const balanceCurrency = (balance?.currency || displayCurrency) as Currency;
  const hasPendingRequest = requests.some(r => r.status === 'pending' || r.status === 'processing');

  // Fee breakdown (in display currency cents)
  const enteredAmountDisplayCents = Math.round((parseFloat(form.amount) || 0) * 100);
  const isMobileMoney = form.payment_method === 'mobile_money';
  const withdrawalFeeCents = Math.round(enteredAmountDisplayCents * feeRates.withdrawal);
  const totalFeeCents = withdrawalFeeCents;
  const netReceivedCents = enteredAmountDisplayCents - totalFeeCents;

  if (loading) {
    return (
      <Card>
        <CardContent className="p-8">
          <div className="flex items-center justify-center">
            <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Balance Card */}
      <Card className="border-2 border-primary/20 bg-gradient-to-br from-primary/5 to-primary/10">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Wallet className="h-5 w-5" />
            Solde disponible pour retrait
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-4xl font-bold text-green-600 mb-4">
            {formatPriceWithCurrency(availableAmount, balance?.currency || 'CDF', displayCurrency, convertPrice)}
          </div>
          <Button 
            onClick={() => setShowRequestDialog(true)}
            disabled={availableAmount <= 0 || hasPendingRequest}
            size="lg"
            className="w-full sm:w-auto"
          >
            <Plus className="h-4 w-4 mr-2" />
            Demander un retrait
          </Button>
          {hasPendingRequest && (
            <p className="text-sm text-muted-foreground mt-2">
              <AlertCircle className="h-4 w-4 inline mr-1" />
              Vous avez déjà une demande en cours
            </p>
          )}
        </CardContent>
      </Card>

      {/* Requests History */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Historique des retraits</CardTitle>
              <CardDescription>Toutes vos demandes de retrait</CardDescription>
            </div>
            <Button variant="outline" size="sm" onClick={fetchRequests}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Actualiser
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {requests.length === 0 ? (
            <div className="text-center py-12">
              <DollarSign className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">Aucune demande de retrait</p>
              <p className="text-sm text-muted-foreground mt-1">
                Vos demandes de retrait apparaîtront ici
              </p>
            </div>
          ) : (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Montant</TableHead>
                    <TableHead>Méthode</TableHead>
                    <TableHead>Statut</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {requests.map((request) => (
                    <TableRow key={request.id}>
                      <TableCell className="font-medium">
                        {new Date(request.requested_at).toLocaleDateString('fr-FR', {
                          day: '2-digit',
                          month: 'short',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </TableCell>
                      <TableCell className="font-bold">
                        {formatPriceWithCurrency(
                          request.amount_cents,
                          request.currency,
                          displayCurrency,
                          convertPrice
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {request.payment_method === 'bank_transfer' && (
                            <>
                              <CreditCard className="h-4 w-4" />
                              <span>Banque</span>
                            </>
                          )}
                          {request.payment_method === 'mobile_money' && (
                            <>
                              <Smartphone className="h-4 w-4" />
                              <span>Mobile Money</span>
                            </>
                          )}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {request.payment_method === 'bank_transfer' && request.account_number}
                          {request.payment_method === 'mobile_money' && request.mobile_money_number}
                        </div>
                      </TableCell>
                      <TableCell>
                        {getStatusBadge(request.status)}
                        {request.rejection_reason && (
                          <div className="text-xs text-destructive mt-1">
                            {request.rejection_reason}
                          </div>
                        )}
                        {request.transaction_reference && (
                          <div className="text-xs text-muted-foreground mt-1">
                            Réf: {request.transaction_reference}
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        {request.status === 'pending' && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleCancelRequest(request.id)}
                          >
                            Annuler
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Request Dialog */}
      <Dialog open={showRequestDialog} onOpenChange={setShowRequestDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Nouvelle demande de retrait</DialogTitle>
            <DialogDescription>
              Remplissez les informations pour demander un retrait de vos gains
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Amount */}
            <div className="space-y-2">
              <Label htmlFor="amount">Montant à retirer</Label>
              <div className="relative">
                <Input
                  id="amount"
                  type="number"
                  step="0.01"
                  placeholder="0.00"
                  value={form.amount}
                  onChange={(e) => setForm({ ...form, amount: e.target.value })}
                  className="pr-16"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
                  {getCurrencySymbol(displayCurrency)}
                </span>
              </div>
              <p className="text-sm text-muted-foreground">
                Disponible: {formatPriceWithCurrency(availableAmount, balanceCurrency, displayCurrency, convertPrice)}
              </p>
              {balanceCurrency !== displayCurrency && (
                <p className="text-xs text-muted-foreground">
                  Saisie en {getCurrencySymbol(displayCurrency)}. Le retrait sera enregistre en {balanceCurrency}.
                </p>
              )}
              {enteredAmountDisplayCents > 0 && (
                <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-3 space-y-1 text-sm">
                  <div className="flex items-center gap-1 font-medium text-amber-800 mb-2">
                    <Info className="h-4 w-4" />
                    Détail des frais
                  </div>
                  <div className="flex justify-between text-muted-foreground">
                    <span>Montant demandé</span>
                    <span>{formatPrice(enteredAmountDisplayCents, displayCurrency)}</span>
                  </div>
                  <div className="flex justify-between text-muted-foreground">
                    <span>Frais de retrait ({(feeRates.withdrawal * 100).toFixed(1).replace('.0', '')}%)</span>
                    <span className="text-red-500">− {formatPrice(withdrawalFeeCents, displayCurrency)}</span>
                  </div>
                  <div className="flex justify-between font-semibold text-green-700 border-t border-amber-200 pt-1 mt-1">
                    <span>Vous recevrez</span>
                    <span>{formatPrice(netReceivedCents, displayCurrency)} {getCurrencySymbol(displayCurrency)}</span>
                  </div>
                </div>
              )}
            </div>

            {/* Payment Method */}
            <div className="space-y-2">
              <Label htmlFor="payment_method">Méthode de paiement</Label>
              <Select
                value={form.payment_method}
                onValueChange={(v: any) => setForm({ ...form, payment_method: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="mobile_money">Mobile Money</SelectItem>
                  <SelectItem value="bank_transfer">Virement bancaire</SelectItem>
                  <SelectItem value="other">Autre</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Bank Transfer Fields */}
            {form.payment_method === 'bank_transfer' && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="bank_name">Nom de la banque</Label>
                  <Input
                    id="bank_name"
                    value={form.bank_name}
                    onChange={(e) => setForm({ ...form, bank_name: e.target.value })}
                    placeholder="Ex: Rawbank, Equity Bank"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="account_number">Numéro de compte</Label>
                  <Input
                    id="account_number"
                    value={form.account_number}
                    onChange={(e) => setForm({ ...form, account_number: e.target.value })}
                    placeholder="Ex: 12345678901234"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="account_name">Nom du titulaire (societe ou representant)</Label>
                  <Input
                    id="account_name"
                    value={form.account_name}
                    readOnly
                    placeholder="Configure dans Parametres magasin"
                  />
                  <p className="text-xs text-muted-foreground">
                    Ce champ est defini automatiquement avec le nom du representant ou le nom de la societe.
                  </p>
                </div>
              </>
            )}

            {/* Mobile Money Fields */}
            {form.payment_method === 'mobile_money' && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="mobile_number">Numéro de téléphone</Label>
                  <Input
                    id="mobile_number"
                    value={form.mobile_money_number}
                    onChange={(e) => setForm({ ...form, mobile_money_number: e.target.value })}
                    placeholder="Ex: +243 XXX XXX XXX"
                  />
                </div>
              </>
            )}

            {/* Notes */}
            <div className="space-y-2">
              <Label htmlFor="payment_notes">Notes (optionnel)</Label>
              <Textarea
                id="payment_notes"
                value={form.payment_notes}
                onChange={(e) => setForm({ ...form, payment_notes: e.target.value })}
                placeholder="Informations complémentaires..."
                rows={3}
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowRequestDialog(false);
                resetForm();
              }}
            >
              Annuler
            </Button>
            <Button onClick={handleSubmit} disabled={submitting}>
              {submitting ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Soumission...
                </>
              ) : (
                <>
                  <DollarSign className="h-4 w-4 mr-2" />
                  Soumettre la demande
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
